import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, AlertTriangle } from 'lucide-react';
import { supabase, auth } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';

type CallbackStatus = 'loading' | 'success' | 'error';

interface CallbackState {
  status: CallbackStatus;
  message: string;
  details?: string;
}

export const AuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchUser } = useAuthStore();
  const [state, setState] = useState<CallbackState>({
    status: 'loading',
    message: 'Vérification de votre authentification...'
  });

  useEffect(() => {
    handleAuthCallback();
  }, []);

  const handleAuthCallback = async () => {
    try {
      console.log('[AUTH CALLBACK] Début du traitement...');
      console.log('[AUTH CALLBACK] URL complète:', window.location.href);
      console.log('[AUTH CALLBACK] Search params:', Object.fromEntries(searchParams.entries()));
      console.log('[AUTH CALLBACK] Hash:', window.location.hash);

      // Check if this is a password recovery flow
      const type = searchParams.get('type');
      if (type === 'recovery') {
        console.log('[AUTH CALLBACK] Password recovery flow detected');

        // Extract access token from URL hash
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');

        if (accessToken) {
          console.log('[AUTH CALLBACK] Access token found, redirecting to reset password page');
          navigate(`/reset-password?access_token=${accessToken}`, { replace: true });
          return;
        } else {
          console.error('[AUTH CALLBACK] No access token in recovery flow');
          setState({
            status: 'error',
            message: 'Lien de réinitialisation invalide',
            details: 'Le lien de réinitialisation est invalide ou a expiré. Veuillez demander un nouveau lien.'
          });
          return;
        }
      }

      // Vérifier les paramètres d'erreur dans l'URL
      const error = searchParams.get('error');
      const errorCode = searchParams.get('error_code');
      const errorDescription = searchParams.get('error_description');

      if (error) {
        console.error('[AUTH CALLBACK] Erreur dans URL:', { error, errorCode, errorDescription });

        let userMessage = 'Erreur d\'authentification';
        let userDetails = errorDescription || error;

        switch (errorCode) {
          case 'otp_expired':
            userMessage = 'Lien de confirmation expiré';
            userDetails = 'Le lien de confirmation a expiré. Veuillez vous inscrire à nouveau ou demander un nouveau lien.';
            break;
          case 'access_denied':
            userMessage = 'Accès refusé';
            userDetails = 'Le lien de confirmation est invalide ou a déjà été utilisé.';
            break;
          case 'invalid_request':
            userMessage = 'Requête invalide';
            userDetails = 'Le lien de confirmation est malformé. Veuillez réessayer l\'inscription.';
            break;
        }

        setState({
          status: 'error',
          message: userMessage,
          details: userDetails
        });
        return;
      }

      // Gestion du flow PKCE (code dans les paramètres)
      const code = searchParams.get('code');
      if (code) {
        console.log('[AUTH CALLBACK] Code PKCE détecté, échange en cours...');
        
        try {
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          
          if (exchangeError) {
            console.error('[AUTH CALLBACK] Erreur échange code:', exchangeError);
            throw new Error(`Erreur lors de l'échange du code: ${exchangeError.message}`);
          }
          
          if (data.session) {
            console.log('[AUTH CALLBACK] Session PKCE créée avec succès');
            await handleSuccessfulAuth(data.session.user);
            return;
          }
        } catch (exchangeError) {
          console.error('[AUTH CALLBACK] Échec échange PKCE:', exchangeError);
          throw exchangeError;
        }
      }

      // Gestion du flow implicit (token dans le hash)
      if (window.location.hash) {
        console.log('[AUTH CALLBACK] Hash détecté, vérification session...');
        
        // Laisser Supabase gérer automatiquement le hash
        const { session, error: sessionError } = await auth.getSession();
        
        if (sessionError) {
          console.error('[AUTH CALLBACK] Erreur session depuis hash:', sessionError);
          throw sessionError;
        }
        
        if (session?.user) {
          console.log('[AUTH CALLBACK] Session implicit récupérée');
          await handleSuccessfulAuth(session.user);
          return;
        }
      }

      // Vérification session existante
      console.log('[AUTH CALLBACK] Vérification session existante...');
      const { session, error: sessionError } = await auth.getSession();
      
      if (sessionError) {
        console.error('[AUTH CALLBACK] Erreur session existante:', sessionError);
        throw sessionError;
      }
      
      if (session?.user) {
        console.log('[AUTH CALLBACK] Session existante trouvée');
        await handleSuccessfulAuth(session.user);
        return;
      }

      // Aucune session trouvée
      throw new Error('Aucune session d\'authentification trouvée. Le lien peut être expiré ou invalide.');
      
    } catch (error: any) {
      console.error('[AUTH CALLBACK] Erreur finale:', error);
      
      setState({
        status: 'error',
        message: 'Erreur de confirmation',
        details: error.message || 'Une erreur inattendue s\'est produite lors de la confirmation.'
      });
    }
  };

  const handleSuccessfulAuth = async (authUser: any) => {
    try {
      console.log('[AUTH CALLBACK] Utilisateur authentifié:', authUser.id, authUser.email);
      
      setState({
        status: 'loading',
        message: 'Création de votre profil...'
      });

      // Vérifier si le profil existe déjà
      const { data: existingProfile, error: profileCheckError } = await supabase
        .from('users')
        .select('id, profile_type')
        .eq('id', authUser.id)
        .maybeSingle();

      if (profileCheckError) {
        console.warn('[AUTH CALLBACK] Erreur vérification profil:', profileCheckError);
      }

      if (!existingProfile) {
        console.log('[AUTH CALLBACK] Création du profil utilisateur...');
        
        // Déterminer le type de profil basé sur l'email
        let profileType = 'PORTEUR';
        let fullName = authUser.user_metadata?.full_name || '';
        let organization = '';
        
        const email = authUser.email?.toLowerCase() || '';
        
        if (email.includes('@cress-guadeloupe.org')) {
          organization = 'CRESS Guadeloupe';
          if (email.includes('direction')) {
            profileType = 'ADMIN';
            fullName = fullName || 'Direction CRESS';
          } else if (email.includes('coordination')) {
            profileType = 'COORDINATEUR';
            fullName = fullName || 'Coordination CRESS';
          } else if (email.includes('accompagnement')) {
            profileType = 'ACCOMPAGNATEUR';
            fullName = fullName || 'Accompagnement CRESS';
          }
        } else if (email.includes('@region-guadeloupe.fr')) {
          profileType = 'FINANCEUR';
          organization = 'Région Guadeloupe';
          fullName = fullName || 'Financeur Région';
        } else if (email.includes('@adeig.org')) {
          profileType = 'ACCOMPAGNATEUR';
          organization = 'ADEIG';
          fullName = fullName || 'Accompagnateur ADEIG';
        } else if (email.includes('@pfil-guadeloupe.org')) {
          profileType = 'ACCOMPAGNATEUR';
          organization = 'PFIL Guadeloupe';
          fullName = fullName || 'Accompagnateur PFIL';
        }

        // Créer le profil
        const { error: createProfileError } = await supabase
          .from('users')
          .insert({
            id: authUser.id,
            email: authUser.email,
            profile_type: profileType,
            full_name: fullName || null,
            phone: authUser.user_metadata?.phone || null,
            organization: organization || authUser.user_metadata?.organization || null,
            address: authUser.user_metadata?.address || null,
            city: authUser.user_metadata?.city || null,
            postal_code: authUser.user_metadata?.postal_code || null
          });

        if (createProfileError) {
          console.error('[AUTH CALLBACK] Erreur création profil:', createProfileError);
          // Ne pas bloquer - l'utilisateur peut créer son profil plus tard
          console.warn('[AUTH CALLBACK] Profil non créé, mais connexion autorisée');
        } else {
          console.log('[AUTH CALLBACK] Profil créé avec succès:', profileType);
        }
      } else {
        console.log('[AUTH CALLBACK] Profil existant trouvé:', existingProfile.profile_type);
      }

      // Mettre à jour le store auth
      await fetchUser();
      
      setState({
        status: 'success',
        message: 'Authentification réussie !',
        details: 'Redirection vers votre tableau de bord...'
      });
      
      // Nettoyer l'URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Redirection après succès
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2000);
      
    } catch (error: any) {
      console.error('[AUTH CALLBACK] Erreur handleSuccessfulAuth:', error);
      setState({
        status: 'error',
        message: 'Erreur de configuration du profil',
        details: error.message || 'Impossible de configurer votre profil utilisateur.'
      });
    }
  };

  const handleRetry = () => {
    setState({
      status: 'loading',
      message: 'Nouvelle tentative...'
    });
    handleAuthCallback();
  };

  const getStatusIcon = () => {
    switch (state.status) {
      case 'loading':
        return <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />;
      case 'success':
        return <CheckCircle className="w-12 h-12 text-green-600" />;
      case 'error':
        return <XCircle className="w-12 h-12 text-red-600" />;
    }
  };

  const getStatusColor = () => {
    switch (state.status) {
      case 'loading':
        return 'text-blue-800';
      case 'success':
        return 'text-green-800';
      case 'error':
        return 'text-red-800';
    }
  };

  const getBackgroundColor = () => {
    switch (state.status) {
      case 'loading':
        return 'bg-blue-50 border-blue-200';
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Status Icon */}
          <div className="mb-6">
            {getStatusIcon()}
          </div>

          {/* Status Message */}
          <div className={`p-4 rounded-lg border mb-6 ${getBackgroundColor()}`}>
            <h1 className={`text-xl font-bold mb-2 ${getStatusColor()}`}>
              {state.message}
            </h1>
            {state.details && (
              <p className={`text-sm ${getStatusColor()}`}>
                {state.details}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-3">
            {state.status === 'error' && (
              <>
                <button
                  onClick={handleRetry}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Réessayer
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Retour à la connexion
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="w-full px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Créer un nouveau compte
                </button>
              </>
            )}
            
            {state.status === 'success' && (
              <div className="flex items-center justify-center space-x-2 text-green-600">
                <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Redirection en cours...</span>
              </div>
            )}
          </div>
        </div>

        {/* Help */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Besoin d'aide ?</p>
          <a 
            href="mailto:support@cress-guadeloupe.org" 
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            support@cress-guadeloupe.org
          </a>
        </div>

        {/* Debug Info (Dev only) */}
        {import.meta.env.DEV && (
          <div className="mt-4 p-3 bg-gray-100 rounded-lg text-left">
            <h3 className="font-medium text-gray-900 mb-2">Debug Info:</h3>
            <div className="text-xs text-gray-600 space-y-1">
              <div>URL: {window.location.href}</div>
              <div>Hash: {window.location.hash || 'aucun'}</div>
              <div>Search: {window.location.search || 'aucun'}</div>
              <div>Code: {searchParams.get('code') || 'aucun'}</div>
              <div>Error: {searchParams.get('error') || 'aucun'}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};